import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  ToggleLeft, 
  ToggleRight, 
  DollarSign, 
  Percent,
  Save,
  RefreshCw,
  AlertCircle,
  TrendingUp
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const AdminControls = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Platform Controls
  const [platformSettings, setPlatformSettings] = useState({
    registrationEnabled: true,
    tradingEnabled: true,
    withdrawalsEnabled: true,
    depositsEnabled: true,
    maintenanceMode: false,
    kycRequired: true,
    binaryTradingEnabled: true,
    aiArbitrageEnabled: true
  });

  // Financial Controls
  const [financialSettings, setFinancialSettings] = useState({
    withdrawalFeePercent: 2.5,
    minWithdrawalAmount: 10,
    maxWithdrawalAmount: 50000,
    minDepositAmount: 10,
    maxDepositAmount: 100000,
    minTradeAmount: 5,
    maxTradeAmount: 10000,
    dailyWithdrawalLimit: 25000
  });

  // Binary Trading Controls
  const [binarySettings, setBinarySettings] = useState({
    minDuration: 30,
    maxDuration: 720,
    minAmount: 5,
    maxAmount: 1000,
    maxROI: 35,
    defaultROI: 25
  });

  // AI Arbitrage Controls
  const [arbitrageSettings, setArbitrageSettings] = useState({
    minInvestment: 100,
    maxInvestment: 50000,
    minPeriod: 1,
    maxPeriod: 45,
    silverROI: '2-4',
    goldROI: '4-6',
    diamondROI: '6-8',
    platinumROI: '8-10',
    vipROI: '10-15'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token');
      const BACKEND_URL = '';

      const response = await fetch(`${BACKEND_URL}/api/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.platform) setPlatformSettings(data.platform);
        if (data.financial) setFinancialSettings(data.financial);
        if (data.binary) setBinarySettings(data.binary);
        if (data.arbitrage) setArbitrageSettings(data.arbitrage);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token');
      const BACKEND_URL = '';

      const response = await fetch(`${BACKEND_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          platform: platformSettings,
          financial: financialSettings,
          binary: binarySettings,
          arbitrage: arbitrageSettings
        })
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Settings saved successfully'
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to save settings',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const ToggleSwitch = ({ enabled, onChange, label }) => (
    <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-cyan-500/20">
      <span className="text-white font-semibold">{label}</span>
      <button
        onClick={() => onChange(!enabled)}
        className={`relative w-14 h-7 rounded-full transition-colors ${
          enabled ? 'bg-green-500' : 'bg-red-500'
        }`}
      >
        <div
          className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
            enabled ? 'translate-x-8' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-cyan-400 font-semibold">Loading Controls...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Platform Controls</h1>
          <p className="text-cyan-400">Manage all platform features and settings</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchSettings}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 transition-all"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={saveSettings}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white font-semibold transition-all disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="bg-orange-900/20 border border-orange-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-orange-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-orange-400 font-semibold mb-1">Important Notice</h3>
            <p className="text-orange-300 text-sm">
              Changes to these settings will affect all users immediately. Make sure you understand the impact before saving changes.
            </p>
          </div>
        </div>
      </div>

      {/* Platform Controls */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5 text-cyan-400" />
          Platform Features
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ToggleSwitch
            enabled={platformSettings.registrationEnabled}
            onChange={(val) => setPlatformSettings({...platformSettings, registrationEnabled: val})}
            label="User Registration"
          />
          <ToggleSwitch
            enabled={platformSettings.tradingEnabled}
            onChange={(val) => setPlatformSettings({...platformSettings, tradingEnabled: val})}
            label="Trading System"
          />
          <ToggleSwitch
            enabled={platformSettings.withdrawalsEnabled}
            onChange={(val) => setPlatformSettings({...platformSettings, withdrawalsEnabled: val})}
            label="Withdrawals"
          />
          <ToggleSwitch
            enabled={platformSettings.depositsEnabled}
            onChange={(val) => setPlatformSettings({...platformSettings, depositsEnabled: val})}
            label="Deposits"
          />
          <ToggleSwitch
            enabled={platformSettings.binaryTradingEnabled}
            onChange={(val) => setPlatformSettings({...platformSettings, binaryTradingEnabled: val})}
            label="Binary Trading"
          />
          <ToggleSwitch
            enabled={platformSettings.aiArbitrageEnabled}
            onChange={(val) => setPlatformSettings({...platformSettings, aiArbitrageEnabled: val})}
            label="AI Arbitrage"
          />
          <ToggleSwitch
            enabled={platformSettings.kycRequired}
            onChange={(val) => setPlatformSettings({...platformSettings, kycRequired: val})}
            label="KYC Verification Required"
          />
          <ToggleSwitch
            enabled={platformSettings.maintenanceMode}
            onChange={(val) => setPlatformSettings({...platformSettings, maintenanceMode: val})}
            label="Maintenance Mode"
          />
        </div>
      </div>

      {/* Financial Controls */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-400" />
          Financial Settings
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Withdrawal Fee (%)
            </label>
            <input
              type="number"
              step="0.1"
              value={financialSettings.withdrawalFeePercent}
              onChange={(e) => setFinancialSettings({...financialSettings, withdrawalFeePercent: parseFloat(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Min Withdrawal ($)
            </label>
            <input
              type="number"
              value={financialSettings.minWithdrawalAmount}
              onChange={(e) => setFinancialSettings({...financialSettings, minWithdrawalAmount: parseFloat(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Max Withdrawal ($)
            </label>
            <input
              type="number"
              value={financialSettings.maxWithdrawalAmount}
              onChange={(e) => setFinancialSettings({...financialSettings, maxWithdrawalAmount: parseFloat(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Daily Withdrawal Limit ($)
            </label>
            <input
              type="number"
              value={financialSettings.dailyWithdrawalLimit}
              onChange={(e) => setFinancialSettings({...financialSettings, dailyWithdrawalLimit: parseFloat(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Min Deposit ($)
            </label>
            <input
              type="number"
              value={financialSettings.minDepositAmount}
              onChange={(e) => setFinancialSettings({...financialSettings, minDepositAmount: parseFloat(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Max Deposit ($)
            </label>
            <input
              type="number"
              value={financialSettings.maxDepositAmount}
              onChange={(e) => setFinancialSettings({...financialSettings, maxDepositAmount: parseFloat(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Min Trade Amount ($)
            </label>
            <input
              type="number"
              value={financialSettings.minTradeAmount}
              onChange={(e) => setFinancialSettings({...financialSettings, minTradeAmount: parseFloat(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Max Trade Amount ($)
            </label>
            <input
              type="number"
              value={financialSettings.maxTradeAmount}
              onChange={(e) => setFinancialSettings({...financialSettings, maxTradeAmount: parseFloat(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
        </div>
      </div>

      {/* Binary Trading Controls */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Percent className="w-5 h-5 text-purple-400" />
          Binary Trading Settings
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Min Duration (seconds)
            </label>
            <input
              type="number"
              value={binarySettings.minDuration}
              onChange={(e) => setBinarySettings({...binarySettings, minDuration: parseInt(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Max Duration (seconds)
            </label>
            <input
              type="number"
              value={binarySettings.maxDuration}
              onChange={(e) => setBinarySettings({...binarySettings, maxDuration: parseInt(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Min Amount ($)
            </label>
            <input
              type="number"
              value={binarySettings.minAmount}
              onChange={(e) => setBinarySettings({...binarySettings, minAmount: parseFloat(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Max Amount ($)
            </label>
            <input
              type="number"
              value={binarySettings.maxAmount}
              onChange={(e) => setBinarySettings({...binarySettings, maxAmount: parseFloat(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Max ROI (%)
            </label>
            <input
              type="number"
              value={binarySettings.maxROI}
              onChange={(e) => setBinarySettings({...binarySettings, maxROI: parseInt(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Default ROI (%)
            </label>
            <input
              type="number"
              value={binarySettings.defaultROI}
              onChange={(e) => setBinarySettings({...binarySettings, defaultROI: parseInt(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
        </div>
      </div>

      {/* AI Arbitrage Controls */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-400" />
          AI Arbitrage Settings
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Min Investment ($)
            </label>
            <input
              type="number"
              value={arbitrageSettings.minInvestment}
              onChange={(e) => setArbitrageSettings({...arbitrageSettings, minInvestment: parseFloat(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Max Investment ($)
            </label>
            <input
              type="number"
              value={arbitrageSettings.maxInvestment}
              onChange={(e) => setArbitrageSettings({...arbitrageSettings, maxInvestment: parseFloat(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Min Period (days)
            </label>
            <input
              type="number"
              value={arbitrageSettings.minPeriod}
              onChange={(e) => setArbitrageSettings({...arbitrageSettings, minPeriod: parseInt(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">
              Max Period (days)
            </label>
            <input
              type="number"
              value={arbitrageSettings.maxPeriod}
              onChange={(e) => setArbitrageSettings({...arbitrageSettings, maxPeriod: parseInt(e.target.value)})}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
        </div>
        
        <h3 className="text-white font-semibold mb-3">Package ROI Ranges (%)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">Silver</label>
            <input
              type="text"
              value={arbitrageSettings.silverROI}
              onChange={(e) => setArbitrageSettings({...arbitrageSettings, silverROI: e.target.value})}
              placeholder="e.g., 2-4"
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">Gold</label>
            <input
              type="text"
              value={arbitrageSettings.goldROI}
              onChange={(e) => setArbitrageSettings({...arbitrageSettings, goldROI: e.target.value})}
              placeholder="e.g., 4-6"
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">Diamond</label>
            <input
              type="text"
              value={arbitrageSettings.diamondROI}
              onChange={(e) => setArbitrageSettings({...arbitrageSettings, diamondROI: e.target.value})}
              placeholder="e.g., 6-8"
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">Platinum</label>
            <input
              type="text"
              value={arbitrageSettings.platinumROI}
              onChange={(e) => setArbitrageSettings({...arbitrageSettings, platinumROI: e.target.value})}
              placeholder="e.g., 8-10"
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">VIP</label>
            <input
              type="text"
              value={arbitrageSettings.vipROI}
              onChange={(e) => setArbitrageSettings({...arbitrageSettings, vipROI: e.target.value})}
              placeholder="e.g., 10-15"
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            />
          </div>
        </div>
      </div>

      {/* Save Button (Bottom) */}
      <div className="flex justify-end">
        <button
          onClick={saveSettings}
          disabled={saving}
          className="flex items-center gap-2 px-8 py-3 rounded-lg bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white font-bold text-lg transition-all disabled:opacity-50"
        >
          <Save className="w-5 h-5" />
          {saving ? 'Saving Changes...' : 'Save All Settings'}
        </button>
      </div>
    </div>
  );
};

export default AdminControls;
